import React, { useState, useEffect } from "react";
import { Login, useIsSignedIn } from "@microsoft/mgt-react";
import { ResponseType } from "@microsoft/microsoft-graph-client";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import onenote from "../assets/images/onenote.png";
import { useStore, RFState } from "../App/store";
import ReactFlow, { useStoreApi } from "reactflow";
import { shallow } from "zustand/shallow";
import { OneNotePage, OneNotePages } from "../types/types";
import { ensureSignedIn, providerIsSignedIn } from "../auth/microsoftAuth";

const selector = (state: RFState) => ({
  setOneNotePages: state.setOneNotePages,
});

interface OneNoteIntegrationModalProps {
  show: boolean;
  onClose: () => void;
}

const OneNoteIntegrationModal: React.FC<OneNoteIntegrationModalProps> = ({
  show,
  onClose,
}) => {
  const [step, setStep] = useState(1);
  const [selectedNotebook, setSelectedNotebook] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [selectedSectionName, setSelectedSectionName] = useState<string | null>(
    null
  );
  const [notebooks, setNotebooks] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [loading, setLoading] = useState(false); // Add loading state
  const isSignedIn = useIsSignedIn();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const store = useStoreApi();
  const { setOneNotePages } = useStore(selector, shallow);

  useEffect(() => {
    if (show) {
      setStep(1);
      setSelectedNotebook(null);
      setSelectedSection(null);
      setSelectedSectionName("OneNote Section");
      if (isSignedIn) {
        fetchNotebooks();
        setStep(2);
      }
    }
  }, [show]);

  useEffect(() => {
    const handleRedirect = () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("modal") === "onenote") {
        setStep(2);
      }
    };

    handleRedirect();
    window.addEventListener("popstate", handleRedirect);

    return () => {
      window.removeEventListener("popstate", handleRedirect);
    };
  }, []);

  const handleNextStep = async () => {
    setLoading(true);
    try {
      if (step === 1 && isSignedIn) {
        await fetchNotebooks();
        setStep(2);
      } else if (step === 2 && selectedNotebook) {
        await fetchSections(selectedNotebook);
        setStep(3);
      } else if (step === 3 && selectedSection) {
        if (selectedNotebook && selectedSection) {
          const pages: OneNotePages = await fetchPages(
            selectedNotebook,
            selectedSection
          );
          const pagesWithContent: OneNotePages = await fetchPagesContent(pages);

          setOneNotePages(pagesWithContent);
          navigate(`/flow/new?template=onenote`);
          onClose();
        }
      }
    } catch (error) {
      console.error("Error during step navigation:", error);
    } finally {
      setLoading(false); // End loading
    }
  };

  const fetchPagesContent = async (pages: OneNotePages) => {
    try {
      const provider = await ensureSignedIn();

      if (provider && providerIsSignedIn(provider.state)) {
        const graphClient = provider.graph.client;
        const pagesWithContent = pages.map(async (page) => {
          const response = await graphClient
            .api(`/me/onenote/pages/${page.id}/content`)
            .responseType(ResponseType.TEXT)
            .get();
          return {
            ...page,
            content: await getResponseBody(response),
          };
        });

        const resolvedPages = await Promise.all(pagesWithContent);
        return resolvedPages;
      } else {
        return pages;
      }
    } catch (error) {
      console.error("Error fetching notebooks:", error);
      return pages;
    }
  };

  const getResponseBody = async (response: any) => {
    if (response) {
      // Parse the full HTML and extract the body content
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(response, 'text/html');
      const bodyContent = htmlDoc.body?.innerHTML || ''; // Extract content inside <body>
      // // Convert the body content to markdown if needed
      // let bodyContent = convertToMarkdownIfNeeded(response);
      // bodyContent = bodyContent.replace(/<br\s*\/?>/gi, "\n"); // Replace <br> with newlines
      // // Remove <img ...> tags from the content
      // bodyContent = bodyContent.replace(/<img[^>]*>/gi, "");
      // // Return the content or an empty string if no content is found

      // bodyContent = decodeHtmlEntities(bodyContent);

      // bodyContent = bodyContent
      //   // Remove links with missing name or URL (e.g., [N/A](), [](), [text])
      //   .replace(/\[(?:N\/A|)\]\(\)/gi, "") // Remove [N/A]() or []()
      //   .replace(/\[\]\([^)]+\)/g, "") // Remove [](<anything>)
      //   .replace(/\[(.*?)\]\((?!https?:\/\/)[^)]+\)/g, "$1") // remove invalid links (non-http)
      //   .replace(/\[(.*?)\]\((https?:\/\/[^)]+)\)/g, (_match, text, url) => {
      //     // ensure markdown is clean and consistent
      //     return `[${text}](${url})`;
      //   });

      console.log("bodyContent", bodyContent);
      return bodyContent || ""; // Ensure fallback if <body> is empty
    }
  };

  const fetchNotebooks = async () => {
    setLoading(true); // Start loading
    try {
      const provider = await ensureSignedIn();

      if (provider && providerIsSignedIn(provider.state)) {
        const graphClient = provider.graph.client;
        const response = await graphClient.api("/me/onenote/notebooks").get();
        setNotebooks(response.value);
      } else {
        setNotebooks([]);
      }
    } catch (error) {
      console.error("Error fetching notebooks:", error);
      setNotebooks([]);
    } finally {
      setLoading(false); // End loading
    }
  };

  const fetchSections = async (notebookId: string) => {
    setLoading(true); // Start loading
    try {
      const provider = await ensureSignedIn();

      if (provider && providerIsSignedIn(provider.state)) {
        const graphClient = provider.graph.client;
        const response = await graphClient
          .api(`/me/onenote/notebooks/${notebookId}/sections`)
          .get();
        setSections(response.value);
      } else {
        setSections([]);
      }
    } finally {
      setLoading(false); // End loading
    }
  };

  const fetchPages = async (notebookId: string, sectionId: string) => {
    setLoading(true); // Start loading
    try {
      const provider = await ensureSignedIn();

      if (provider && providerIsSignedIn(provider.state)) {
        const graphClient = provider.graph.client;
        const response = await graphClient
          .api(`/me/onenote/sections/${sectionId}/pages`)
          .get();
        return response.value;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
      return [];
    } finally {
      //setLoading(false); // End loading disabled, since it will be done when flow is opened
    }
  };
  const handlePreviousStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleNotebookSelect = (notebook: string) => {
    setSelectedNotebook(notebook);
  };

  const handleSectionSelect = (section: string, name: string) => {
    setSelectedSection(section);
    setSelectedSectionName(name);
  };

  if (!show) return null;

  return (
    <div className="onenote-modal">
      <div className="onenote-modal-content">
        <span className="close" onClick={onClose}>
          &times;
        </span>
        <h1 className="onenote-modal-title">Integration Guide</h1>
        {loading && (
          <div className="onenote-progress-overlay">
            <div className="onenote-progress-indicator"></div>
            <div className="onenote-working">
              <div>Getting</div>
              <div>your OneNote</div>
              <div>data...</div>
            </div>
          </div>
        )}
        {!loading && step === 1 && (
          <div className="step-content">
            <img
              src={onenote}
              alt="OneNote"
              style={{ marginTop: "50px", height: "100px" }}
            />
            <h2 className="step1-title" style={{ marginTop: "10px" }}>
              Sign in to access OneNote
            </h2>
            <div className="login-container">
              {isSignedIn ? (
                <p style={{ fontSize: "16px" }}>Already signed In</p>
              ) : (
                <Login />
              )}
            </div>
            <div className="button-container">
              <button
                className="onenote-button"
                onClick={handleNextStep}
                disabled={!isSignedIn}
              >
                Next
              </button>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="step-content">
            <h2 className="step1-title">Select a Notebook:</h2>
            <div className="list-container">
              {notebooks.map((notebook) => (
                <div
                  key={notebook.id}
                  className={`list-item ${
                    selectedNotebook === notebook.id ? "selected" : ""
                  }`}
                  onClick={() => handleNotebookSelect(notebook.id)}
                >
                  {notebook.displayName}
                </div>
              ))}
            </div>
            <div className="button-container">
              <button className="onenote-button" onClick={handlePreviousStep}>
                Previous
              </button>
              <button
                className="onenote-button"
                onClick={handleNextStep}
                disabled={!selectedNotebook}
              >
                Next
              </button>
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="step-content">
            <h2 className="step1-title">Select a Section:</h2>
            <div className="list-container">
              {sections.map((section) => (
                <div
                  key={section.id}
                  className={`list-item ${
                    selectedSection === section.id ? "selected" : ""
                  }`}
                  onClick={() =>
                    handleSectionSelect(section.id, section.displayName)
                  }
                >
                  {section.displayName}
                </div>
              ))}
            </div>
            <div className="button-container">
              <button className="onenote-button" onClick={handlePreviousStep}>
                Previous
              </button>
              <button
                className="onenote-button"
                onClick={handleNextStep}
                disabled={!selectedSection}
              >
                Connect
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OneNoteIntegrationModal;
